import React, { useContext, useState, useEffect } from "react";
import ModalMain from "../../../../components/Modal/ModalMain";
import ModalHeader from "../../../../components/Modal/ModalHeader";
import Firm2FirmExportCsvBody from "../../../Firm2Firm/components/ExportCsv/Firm2FirmExportCsvBody";
import { useTranslation } from "react-i18next";
import ModalFooter from "../../../../components/Modal/ModalFooter";
import { useFormik } from "formik";
import {
  pacsQueueExportCsvInitiate,
} from "../../../../service/PacsQueueService";
import toastContext from "../../../../context/toast/ToastContext";
import { TOAST_TYPE } from "../../../../constants/Constants";

const PacsQueueExportCsvModal = (props) => {
  const {
    show,
    onClose,
    onExportInitiated,
  } = props;
  const [t] = useTranslation();
  const [filterObj, setFilterObj] = useState({
    fromDate: null,
    toDate: null,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [downloadRunning, setDownloadRunning] = useState(false);
  const { showProgressToast, showToastv2 } = useContext(toastContext);

  const formatDateForApi = (date) => {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const checkForDateRange = (fromStr, toStr) => {
    const [fd, fm, fy] = fromStr.split("/");
    const [td, tm, ty] = toStr.split("/");
    const from = new Date(fy, fm - 1, fd);
    const to = new Date(ty, tm - 1, td);
    return from > to;
  };

  const checkForMaxRange = (fromStr, toStr) => {
    const [fd, fm, fy] = fromStr.split("/");
    const [td, tm, ty] = toStr.split("/");
    const from = new Date(fy, fm - 1, fd);
    const to = new Date(ty, tm - 1, td);
    const oneDay = 24 * 60 * 60 * 1000;
    const diffDays = Math.round(Math.abs(to - from) / oneDay);
    return diffDays > 45;
  };

  const buildPayload = (values) => {
    return {
      paginationDto: {
        createdOnFrom: formatDateForApi(filterObj.fromDate),
        createdOnTo: formatDateForApi(filterObj.toDate),
        status: [0, 1, 2, 3, 4, 5],
      },
    };
  };

  const hideModal = () => {
    onClose();
    setValues({
      fromDate: "",
      toDate: "",
    });
    setErrorMessage("");
  };

  const { values, errors, handleSubmit, setValues, setErrors } = useFormik({
    initialValues: {
      fromDate: null,
      toDate: null,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, actions) => {
      const payload = buildPayload(values);
      setErrorMessage("");
      setErrors({});

      if (!payload.paginationDto.createdOnFrom && !payload.paginationDto.createdOnTo) {
        const fromError =
          t("export-csv.date-from-required") ||
          "Request Created From date is required.";
        const toError =
          t("export-csv.date-to-required") ||
          "Request Created To date is required.";
        setErrors({ fromDate: fromError, toDate: toError });
        return;
      }
      if (!payload.paginationDto.createdOnFrom) {
        const fromError =
          t("export-csv.date-from-required") ||
          "Request Created From date is required.";
        setErrors({ fromDate: fromError });
        return;
      }
      if (!payload.paginationDto.createdOnTo) {
        const toError =
          t("export-csv.date-to-required") ||
          "Request Created To date is required.";
        setErrors({ toDate: toError });
        return;
      }

      if (
        checkForDateRange(
          payload.paginationDto.createdOnFrom,
          payload.paginationDto.createdOnTo
        )
      ) {
        setErrorMessage(t("error.from-to-date-not-correct"));
        return;
      }

      if (
        checkForMaxRange(
          payload.paginationDto.createdOnFrom,
          payload.paginationDto.createdOnTo
        )
      ) {
        setErrorMessage(t("error.date-difference-greater-than-31"));
        return;
      }

      setDownloadRunning(true);

      const response = await pacsQueueExportCsvInitiate(payload);
      if (response.status === 201) {
        const token = response.data.data.token;
        const id = showProgressToast("Downloading file", 0);
        showToastv2(TOAST_TYPE.INFO, "Downloading started");
        setDownloadRunning(false);
        hideModal();
        onExportInitiated(id, token);
      } else if (response.status === 200) {
        if (response.data.message === "Study not found for selected filters") {
          setErrorMessage("Study not found for selected date range");
          setDownloadRunning(false);
          return;
        } else if (
          response.data.data === "Already the request is being processed"
        ) {
          showToastv2(
            TOAST_TYPE.INFO,
            "Your export request is already being processed"
          );
        } else {
          showToastv2(TOAST_TYPE.INFO, response.data.message || "Export request submitted");
        }
        setDownloadRunning(false);
        hideModal();
      } else {
        setDownloadRunning(false);
        setErrorMessage(
          response.data.message || "Failed to start download"
        );
      }
    },
  });

  useEffect(() => {
    if (show) {
      setFilterObj({ fromDate: null, toDate: null });
      setValues({ fromDate: null, toDate: null });
      setErrorMessage("");
    }
  }, [show]);

  const DefaultButtonList = [
    { title: t("button.cancel"), onclick: hideModal },
  ];

  const PrimaryButtonList = [
    {
      title: t("button.download"),
      disabled: downloadRunning,
      onclick: handleSubmit,
    },
  ];

  const modalDetails = {
    header: (
      <ModalHeader
        updatedModal={true}
        heading={t("export-csv.title")}
        handleCancel={hideModal}
      />
    ),
    body: (
      <Firm2FirmExportCsvBody
        values={values}
        handleChange={() => {}}
        handleSubmit={handleSubmit}
        errors={errors}
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        setValues={setValues}
        setErrors={setErrors}
      />
    ),
    footer: (
      <ModalFooter
        DefaultButtonList={DefaultButtonList}
        PrimaryButtonList={PrimaryButtonList}
      />
    ),
    width: "600px",
  };

  return (
    <>
      {show && (
        <ModalMain
          updatedModal={true}
          modalDetails={modalDetails}
          showModal={show}
        />
      )}
    </>
  );
};

export default PacsQueueExportCsvModal;
